import { getObserver } from '@theme/theme-global';

class ProductKeyFeatures extends HTMLDivElement {
  constructor() {
    super();
    this.onTabChange = this.onTabChange.bind(this);
    this.animToken = 0;
    this.hasEnteredView = false;
    this.activeShapeIndex = 0;
    this.hideDuration = 320;
    this.drawDuration = 1400;
    this.hideTimer = null;
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListener('tabChangeStart', this.onTabChange);
    this.clearHideTimer();
    if (this._observer) {
      this._observer.unobserve(this);
      this._observer = null;
    }
  }

  init() {
    this.shapes = Array.from(this.querySelectorAll('[data-section-shape="true"]'));
    if (!this.shapes.length) return;

    this.shapes.forEach((shape) => {
      const path = shape.querySelector('path');
      if (!path) return;

      const length = this.getPathLength(path);
      path.dataset.pathLength = String(length);
      path.style.strokeDasharray = String(length);
      path.style.strokeDashoffset = String(length);
      path.style.transition = 'none';
      shape.style.opacity = '0';
      shape.classList.remove('is-active');
    });

    this.addEventListener('tabChangeStart', this.onTabChange);

    if (!('IntersectionObserver' in window)) {
      this.hasEnteredView = true;
      this.animateShape(0);
      return;
    }

    this._observer = getObserver({ threshold: 0.15 }, true, false);
    this._observer.observe(this, (entry) => {
      if (!entry.isIntersecting) return;
      this.hasEnteredView = true;
      this.animateShape(0);
    });
  }

  getPathLength(path) {
    if (typeof path.getTotalLength === 'function') {
      const total = path.getTotalLength();
      if (total > 0) return total;
    }

    return Number.parseFloat(path.style.strokeDasharray) || 0;
  }

  getShapeIndexForTab(tabIndex) {
    const count = this.shapes.length;
    if (!count) return 0;
    return ((Number(tabIndex) - 1) % count + count) % count;
  }

  onTabChange(event) {
    if (!this.hasEnteredView) return;
    const tabIndex = event.detail?.index ?? 1;
    this.replayShape(this.getShapeIndexForTab(tabIndex));
  }

  clearHideTimer() {
    if (!this.hideTimer) return;
    clearTimeout(this.hideTimer);
    this.hideTimer = null;
  }

  resetPath(path) {
    if (!path) return;
    const length = Number(path.dataset.pathLength) || this.getPathLength(path);
    path.style.transition = 'none';
    path.style.strokeDasharray = String(length);
    path.style.strokeDashoffset = String(length);
  }

  drawPath(path, token) {
    requestAnimationFrame(() => {
      if (token !== this.animToken || !path) return;
      path.style.transition = `stroke-dashoffset ${this.drawDuration}ms cubic-bezier(0.65, 0, 0.35, 1)`;
      path.style.strokeDashoffset = '0';
    });
  }

  setActiveShape(index) {
    this.shapes.forEach((shape, i) => {
      const isActive = i === index;
      shape.classList.toggle('is-active', isActive);
      shape.style.transition = 'none';
      shape.style.opacity = isActive ? '1' : '0';
    });
    this.activeShapeIndex = index;
  }

  animateShape(index) {
    if (!this.shapes[index]) return;

    const token = ++this.animToken;
    this.clearHideTimer();

    this.shapes.forEach((shape, i) => {
      this.resetPath(shape.querySelector('path'));
      shape.style.transition = 'none';
      shape.style.opacity = '0';
      shape.classList.remove('is-active');
    });

    this.setActiveShape(index);
    const path = this.shapes[index].querySelector('path');
    void path.getBoundingClientRect();
    this.drawPath(path, token);
  }

  replayShape(nextIndex) {
    if (!this.shapes[nextIndex]) return;

    const token = ++this.animToken;
    this.clearHideTimer();

    const current = this.shapes[this.activeShapeIndex];
    if (current) {
      current.style.transition = `opacity ${this.hideDuration}ms ease`;
      current.style.opacity = '0';
    }

    this.hideTimer = setTimeout(() => {
      if (token !== this.animToken) return;

      this.shapes.forEach((shape) => {
        this.resetPath(shape.querySelector('path'));
        shape.style.transition = 'none';
        shape.style.opacity = '0';
        shape.classList.remove('is-active');
      });

      this.setActiveShape(nextIndex);
      const path = this.shapes[nextIndex].querySelector('path');
      void path.getBoundingClientRect();
      this.drawPath(path, token);
    }, this.hideDuration);
  }
}

if (!customElements.get('product-key-features')) {
  customElements.define('product-key-features', ProductKeyFeatures, { extends: 'div' });
}
